import { LoadingButton } from '@mui/lab';
import {
  Checkbox,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Link as MUILink,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Box } from '@mui/system';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';

import { register } from '@app/adapter/user-service';
import { HeadBlock } from '@app/components/Shared/HeadBlock';
import { userIvCodeState } from '@app/domain/user';
import { useSetSnackbar } from '@app/hooks/useSetSnackbar';
import { RegistrationForm, IRegistrationForm } from '@app/schemas/registration';
import { isError } from '@app/utils/error';
import { generateInviteCode } from '@app/utils/user';

export function Register(): ReactElement {
  const navigate = useNavigate();
  const setSnackbar = useSetSnackbar();
  const [ivCodeState, setIvCodeState] = useRecoilState(userIvCodeState);
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();

  const { control, handleSubmit, formState } = useForm<IRegistrationForm>({
    defaultValues: { ...RegistrationForm.defaultValues },
    mode: 'all',
    resolver: RegistrationForm.resolver,
  });

  const location = useLocation();
  const queryParams = useMemo(() => {
    return new URLSearchParams(location.search);
  }, [location.search]);

  useEffect(() => {
    const ivCode = queryParams.get('iv');
    if (ivCode) {
      setIvCodeState(ivCode);
    }
    // eslint-disable-next-line
  }, []);

  const onSubmit = useCallback(
    async (data: IRegistrationForm) => {
      const {
        email,
        passwords: { password },
      } = data;
      try {
        setIsLoading(true);
        await register(email, password, {
          inviteCode: generateInviteCode(),
          invitedCode: queryParams.get('iv') || ivCodeState,
        });
        setIvCodeState('');
        navigate('/waiting-email-verify');
      } catch (e) {
        let message = '登録できませんでした';
        if (isError(e)) {
          console.error(e.message);
          if (e.message.includes('has been registered in system')) {
            message = '既にこのメールアドレスは登録済みです';
          }
        }
        setSnackbar(true, message);
      } finally {
        setIsLoading(false);
      }
    },
    [ivCodeState, navigate, queryParams, setSnackbar, setIvCodeState]
  );

  return (
    <>
      <HeadBlock />
      <Container>
        <Stack spacing={3} sx={{ px: 2, py: 3 }}>
          <Typography
            variant="h5"
            fontWeight={600}
            textAlign="center"
            sx={{ py: 1 }}
          >
            新規会員登録
          </Typography>
          <Typography>
            会員登録することで、応募や検索条件の保存が出来るようになります
          </Typography>
        </Stack>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <FormControl variant="outlined" fullWidth>
              <FormLabel>
                <Typography variant="body2" fontWeight={500}>
                  メールアドレス
                </Typography>
              </FormLabel>
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    error={error ? true : false}
                    helperText={error?.message}
                    margin="dense"
                    placeholder="メールアドレスを入力"
                  />
                )}
              />
            </FormControl>
            <FormControl variant="outlined" fullWidth>
              <FormLabel>
                <Typography variant="body2" fontWeight={500}>
                  パスワード
                </Typography>
              </FormLabel>
              <Controller
                name="passwords.password"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type="password"
                    error={error ? true : false}
                    helperText={error?.message}
                    margin="dense"
                    placeholder="パスワードを入力"
                    autoComplete="new-password"
                  />
                )}
              />
              <Typography
                sx={{ color: theme.palette.grey[600], textAlign: 'left' }}
              >
                8文字以上、英数字含む（記号を除く）
              </Typography>
            </FormControl>
            <FormControl variant="outlined" fullWidth>
              <FormLabel>
                <Typography variant="body2" fontWeight={500}>
                  パスワードを再入力
                </Typography>
              </FormLabel>
              <Controller
                name="passwords.confirmPassword"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type="password"
                    error={error ? true : false}
                    helperText={error?.message}
                    margin="dense"
                    placeholder="パスワードを再入力"
                  />
                )}
              />
            </FormControl>
            <Stack>
              <Controller
                name="userAgreement"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormControl error={error ? true : false}>
                    <FormControlLabel
                      label={
                        <Typography component="div" variant="body2">
                          <Link
                            target="_blank"
                            rel="noopener noreferrer"
                            to="https://med-pro.jp/media.dr/prodoctors_termsofservice/"
                            style={{ textDecoration: 'none' }}
                          >
                            <Typography
                              component="span"
                              color="primary"
                              variant="body2"
                            >
                              利用規約
                            </Typography>
                          </Link>
                          に同意
                        </Typography>
                      }
                      control={<Checkbox {...field} />}
                    />
                    {error ? (
                      <FormHelperText sx={{ ml: 0 }}>
                        {error.message}
                      </FormHelperText>
                    ) : undefined}
                  </FormControl>
                )}
              />
              <Controller
                name="privacyPolicy"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormControl error={error ? true : false}>
                    <FormControlLabel
                      label={
                        <Typography component="div" variant="body2">
                          <Link
                            target="_blank"
                            rel="noopener noreferrer"
                            to="https://med-pro.jp/media.dr/en_privacypolicy/"
                            style={{ textDecoration: 'none' }}
                          >
                            <Typography
                              component="span"
                              color="primary"
                              variant="body2"
                            >
                              プライバシーポリシー
                            </Typography>
                          </Link>
                          に同意
                        </Typography>
                      }
                      control={<Checkbox {...field} />}
                    />
                    {error ? (
                      <FormHelperText sx={{ ml: 0 }}>
                        {error.message}
                      </FormHelperText>
                    ) : undefined}
                  </FormControl>
                )}
              />
            </Stack>
            <LoadingButton
              type="submit"
              variant="contained"
              fullWidth
              disabled={!formState.isValid}
              loading={isLoading}
            >
              登録
            </LoadingButton>
          </Stack>
        </form>
        <Box sx={{ my: 3 }}>
          <Divider sx={{ mb: 3 }} />
          <Typography component="div" align="center" variant="body2">
            すでに会員の方は
            <MUILink component={Link} to="/login" underline="none">
              ログイン
            </MUILink>
            へ
          </Typography>
        </Box>
      </Container>
    </>
  );
}
